import type { PublicShopProfile } from "@/lib/api/public-consumer"

// Branded header shared across the consumer flow + tracking. Branding is
// accent-only: the logo chip uses the shop's primaryColor, everything else
// stays in the emerald/zinc system so the experience reads as "ours, for them"
// rather than a fully reskinned page.
export function ShopHeader({
  shop,
  subtitle,
}: {
  shop: PublicShopProfile
  subtitle?: string
}) {
  return (
    <header className="flex items-center gap-3 py-1">
      <ShopLogo shop={shop} />
      <div className="min-w-0">
        <p className="truncate text-[14px] font-semibold leading-tight text-zinc-900">
          {shop.name}
        </p>
        <p className="truncate text-[13px] leading-tight text-zinc-500">
          {subtitle ?? [shop.city, shop.state].filter(Boolean).join(", ") ?? ""}
        </p>
      </div>
    </header>
  )
}

// White initials unless the shop's colour is too light to read them on; then
// ink. Whichever reads better (WCAG contrast) wins.
function initialsColor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return "#FFFFFF"
  const n = parseInt(m[1], 16)
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  const lum = 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
  const onWhite = 1.05 / (lum + 0.05)
  const onInk = (lum + 0.05) / (0.0119 + 0.05)
  return onWhite >= 4.5 || onWhite >= onInk ? "#FFFFFF" : "#0C2018"
}

export function ShopLogo({
  shop,
  size = "md",
}: {
  shop: PublicShopProfile
  size?: "md" | "lg"
}) {
  const dim = size === "lg" ? "size-12 text-[15px]" : "size-9 text-[13px]"
  if (shop.branding.logoUrl) {
    return (
      <img
        src={shop.branding.logoUrl}
        alt={shop.name}
        className={`${dim.split(" ")[0]} shrink-0 rounded-xl object-cover`}
      />
    )
  }
  return (
    <div
      className={`grid ${dim} shrink-0 place-items-center rounded-xl font-bold shadow-sm`}
      style={{ backgroundColor: shop.branding.primaryColor, color: initialsColor(shop.branding.primaryColor) }}
      aria-hidden
    >
      {shop.branding.logoInitials}
    </div>
  )
}
